<template>
    <div class="content">
        <v-container v-if="!this.date && !this.loading" fluid height="100%">
            <v-row height="100%">
                <!-- DOWNLOADS -->
                <v-col cols="3">
                    <v-card height="100%">
                        <v-card-title align="center">DOWNLOADS</v-card-title>
                        <v-card-item>
                            <v-menu v-if="customEntityList.length">
                                <template v-slot:activator="{ props }">
                                    <v-btn color="info" v-bind="props" width="100%"
                                        >ENTITY DATA TEMPLATE</v-btn
                                    >
                                </template>
                                <v-list>
                                    <v-list-item
                                        v-for="(item, index) in customEntityList"
                                        :key="index"
                                        :value="item.name"
                                        @click="downloadCustomDataExcel(item)"
                                    >
                                        <v-list-item-title>{{
                                            item.display_name || item.name
                                        }}</v-list-item-title>
                                        <template v-slot:append>
                                            <v-tooltip location="right">
                                                <span
                                                    >Download {{ item.name }} Custom Entity Data
                                                    Excel</span
                                                >
                                                <template v-slot:activator="{ props }">
                                                    <v-icon v-bind="props">$download</v-icon>
                                                </template>
                                            </v-tooltip>
                                        </template>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </v-card-item>
                    </v-card>
                </v-col>

                <!-- DATA -->
                <v-col>
                    <v-card>
                        <v-card-title align="center">DATA COLLECTION</v-card-title>
                        <v-row>
                            <!-- CALENDAR COLUMN -->
                            <v-col cols="7">
                                <v-select
                                    v-if="showFilter"
                                    label="Filter Data"
                                    :items="uniqueEntityTypes"
                                    :model-value="filterEntitiesArray"
                                    @update:model-value="filterEntitiesArray = $event"
                                    item-title="label"
                                    item-value="label"
                                    multiple
                                    chips
                                    closable-chips
                                    :item-props="
                                        (item) => ({
                                            color: item.color,
                                        })
                                    "
                                >
                                    <template v-slot:chip="{ props, item }">
                                        <v-chip
                                            v-bind="props"
                                            :color="item.props.color"
                                            close-icon="$close"
                                        >
                                            {{ item.title }}
                                        </v-chip>
                                    </template>
                                </v-select>
                                <v-date-picker
                                    :modelValue="date"
                                    @update:model-value="calendarDateSelectAsDate"
                                    :events="mergeDates"
                                    :event-color="functionEvents"
                                    title="Select a date to enter Well Data"
                                    width="100%"
                                    flat
                                >
                                    <template v-slot:header></template>
                                </v-date-picker>
                            </v-col>

                            <!-- COLLECTED DATA COLUMN -->
                            <v-col>
                                <v-card>
                                    <v-card-title>COLLECTED DATA</v-card-title>
                                    <v-file-input
                                        v-model="legacyOTDataFile"
                                        label="OT Data"
                                        accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                    ></v-file-input>
                                    <v-btn @click="uploadLegacyOTDataFile"
                                        >Upload OT Data File</v-btn
                                    >
                                    <v-list v-if="filteredDateObjArray.length">
                                        <v-list-item
                                            v-for="(dateObj, index) in filteredDateObjArray"
                                            :key="index"
                                            @click="collectedDateSelect(dateObj.handler, dateObj)"
                                        >
                                            <v-row>
                                                <v-col cols="6">
                                                    <v-chip :color="dateObj.color">{{
                                                        dateObj.label
                                                    }}</v-chip>
                                                </v-col>
                                                <v-col>
                                                    {{ dateObj.date }}
                                                    <v-icon
                                                        size="medium"
                                                        v-if="dateObj.source !== 'benchling'"
                                                        @click.stop="deleteByDate(dateObj)"
                                                    >
                                                        $delete
                                                    </v-icon>
                                                    <v-icon
                                                        size="medium"
                                                        v-if="dateObj.source !== 'benchling'"
                                                        @click.stop="
                                                            downloadCustomDataByDate(dateObj)
                                                        "
                                                    >
                                                        $download
                                                    </v-icon>
                                                </v-col>
                                            </v-row>
                                        </v-list-item>
                                    </v-list>
                                    <h3 v-else align="center" style="color: gray; padding-top: 50%">
                                        NO DATA COLLECTED
                                    </h3>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <!-- DATA TABLE -->
        <div v-else>
            <v-toolbar color="#EEEEEE" class="pl-3" height="40px">
                <v-toolbar-title v-if="activeDateObj">
                    {{ activeDateObj.date }} - {{ activeDateObj.label }} Data {{ timeZoneLabel }}
                </v-toolbar-title>
                <v-text-field
                    end
                    max-width="320"
                    type="date"
                    :model-value="date"
                    label="Date Selection"
                    @update:model-value="calendarDateSelectAsString"
                />
            </v-toolbar>
            <v-data-table
                :search="search"
                :headers="headers"
                :items="rawData"
                item-key="id"
                :items-per-page="30"
                class="pa-3"
                style="min-width: min-content"
                :hide-default-footer="!rawData.length"
            >
            </v-data-table>
        </div>

        <v-snackbar v-model="qrAlert.status" :timeout="3000" location="top">
            {{ qrAlert.message }}
        </v-snackbar>

        <!-- FORM COMPONENTS -->
        <container-form v-if="displayContainer"></container-form>
        <well-form v-else :datemeasured="date"></well-form>
    </div>
</template>

<script>
/* eslint-disable vue/no-async-in-computed-properties */

import { defineComponent, nextTick } from "vue";

import _ from "lodash";
import config from "../../config";
import { EventBus } from "../../lib/event-bus.js";
import axios from "../../lib/apiAxios";
import { DateTime } from "luxon";
import { getDaysBetweenDates } from "../../lib/dateUtils";
import WellForm from "../Wells/WellForm";
import ContainerForm from "../Wells/ContainerForm";
import { mapGetters } from "vuex";
import {
    fetchAllMergedEntitySchemasByProtocol,
    uploadLegacyOTDataFile,
} from "@/lib/legacyThingUtils.js";
import { withLoadingScreen } from "@/lib/loading.js";
import { useToast } from "vue-toastification";

export default defineComponent({
    name: "ExperimentData",

    props: {
        search: { type: String },
        experimentData: { type: Object },
        protocolId: { type: String },
        customEntityList: { type: Array },
    },

    components: {
        WellForm,
        ContainerForm,
    },

    setup() {
        return {
            toast: useToast(),
        };
    },

    data() {
        return {
            activeDateObj: null,
            loading: true,
            displayContainer: false,
            displayExperimentDataDownload: false,
            filterEntitiesArray: [],
            outputs: [],
            headers: [],
            rawData: [],
            qrAlert: {
                status: false,
                message: "",
            },
            startDate: null,
            dayDiff: 0,
            selectedCustomEntity: null,
            showFilter: true,
            timeZone: null,
            mangoSchemaData: null,
            legacyOTDataFile: null,
        };
    },

    async created() {
        this.reloadData();

        EventBus.$on("message", (data) => {
            this.qrAlert = { status: true, message: data.message };
            if (data.refresh) this.reloadData();
        });
    },

    computed: {
        ...mapGetters(["isAuthorized"]),
        collectedDataObjArray: function () {
            const benchlingData = this.experimentData.benchlingData;
            const colorArray = ["#73c074", "#8aaf06", "#00a701", "#4e6200", "#32CD32"];
            let dateObjArray = [];

            // Process mango data
            const mangoData = this.experimentData.mangoData;
            // Set flag for source of well data
            const displayBenchlingWells = !_.find(this.mangoSchemaData, {
                name: "experiment_wells",
                parent: "experiment_containers",
            });

            if (mangoData) {
                mangoData.forEach((entity, i) => {
                    dateObjArray = dateObjArray.concat(
                        entity.collection_dates.map((date) => {
                            return {
                                date: date,
                                label: entity.display_name,
                                color: colorArray[i % colorArray.length],
                                entity: entity.name,
                                parent: entity.parent,
                                handler: this.mangoDateSelectHandler,
                                source: "mango",
                            };
                        })
                    );
                });
            }

            // Process benchling data - Ignore benchling well data for 'Field Trials', use Mango
            if (benchlingData.measureDates && displayBenchlingWells) {
                benchlingData.measureDates.forEach((date) => {
                    dateObjArray.push({
                        date: date,
                        label: "Benchling Well",
                        color: "#027545",
                        handler: this.wellDateSelectHandler,
                        source: "benchling",
                    });
                });
            }

            return dateObjArray.sort((a, b) => {
                const date1 = new Date(a.date);
                const date2 = new Date(b.date);
                return date2 > date1 ? 1 : -1;
            });
        },
        mergeDates: function () {
            // Create array of all data dates
            const allDates = _.uniq(_.flatMap(this.filteredDateObjArray, "date"));
            return allDates.sort((a, b) => {
                const date1 = new Date(a.date);
                const date2 = new Date(b.date);
                return date2 > date1 ? 1 : -1;
            });
        },
        date: function () {
            const date = _.get(this, "$route.params.date");
            return date === "" ? undefined : date;
        },
        uniqueEntityTypes: function () {
            return _.uniqBy(this.collectedDataObjArray, "label");
        },
        filteredDateObjArray: function () {
            return _.filter(this.collectedDataObjArray, (dateObj) =>
                this.filterEntitiesArray.includes(dateObj.label)
            );
        },
        timeZoneLabel: function () {
            return this.timeZone ? `(Time Zone: ${this.timeZone})` : "";
        },
    },

    methods: {
        async uploadLegacyOTDataFile() {
            await withLoadingScreen(async () => {
                try {
                    await uploadLegacyOTDataFile(this.legacyOTDataFile);
                    this.legacyOTDataFile = null;
                } catch (err) {
                    console.log(err);
                    this.toast.error(err);
                }
            });
        },
        checkOutputs(outputs) {
            if (outputs === "{}") return false;
            if (outputs === "[]") return false;
            if (outputs === null) return false;
            return true;
        },
        functionEvents(date) {
            // Map dates to event colors
            const calendarMarks = _.map(
                _.filter(this.filteredDateObjArray, { date: date }),
                "color"
            ).slice(0, 9);
            return calendarMarks;
        },
        downloadCustomDataExcel(entity) {
            window.open(
                `${config.apiHost}/experiment/${this.$route.params.code}/excel/customData/${this.protocolId}/${entity.name}/${entity.parent}/${this.$route.params.code}${entity.name}Data.xlsx`,
                "_blank"
            );
            nextTick(() => {
                this.selectedCustomEntity = null;
            });
        },
        reloadData: async function () {
            // Open loading screen
            EventBus.$emit("globalLoadScreen", { status: true, message: "Loading Data..." });
            this.loading = true;
            if (!this.date) {
                try {
                    // Fetch mango entity schemas
                    const schemaRes = await fetchAllMergedEntitySchemasByProtocol(this.protocolId);
                    this.mangoSchemaData = schemaRes.data;

                    const expData = this.experimentData.expData;
                    this.activeDateObj = null;

                    if (expData) {
                        this.startDate = DateTime.fromISO(expData.start_date);
                        this.displayContainer = expData.display_container === 1;
                        EventBus.$emit("protocol_type", { protocolId: expData.id });

                        if (expData.display_plate || expData.functional_team === "Microbial") {
                            // Handle Microbial and Display Plate
                        } else {
                            // Set visibility for download buttons
                            this.displayExperimentDataDownload =
                                this.checkOutputs(expData.outputs) ||
                                this.checkOutputs(expData.container_outputs);
                        }
                        this.timeZone = expData.site_time_zone;

                        this.filterEntitiesArray = _.map(this.uniqueEntityTypes, "label");
                    }
                } catch (err) {
                    console.log(err);
                }
            }
            // Close loading screen
            EventBus.$emit("globalLoadScreen", { status: false, message: null });
            this.loading = false;
        },
        calendarDateSelectAsString: function (date) {
            const dateTime = DateTime.fromISO(date);
            this.calendarDateSelectAsDateTime(dateTime);
        },
        calendarDateSelectAsDate: function (date) {
            const dateTime = DateTime.fromJSDate(date);
            this.calendarDateSelectAsDateTime(dateTime);
        },
        calendarDateSelectAsDateTime: function (dateTime) {
            const dateStr = dateTime.toFormat("yyyy-MM-dd");
            this.$router.push({
                path: `/experiment/${this.$route.params.code}/${this.$route.params.protocolId}/${dateStr}`,
            });
            this.wellDateSelectHandler({ date: dateStr });
        },
        collectedDateSelect: function (handler, dateObj) {
            this.$router.push({
                path: `/experiment/${this.$route.params.code}/${this.$route.params.protocolId}/${dateObj.date}`,
            });
            handler(dateObj);
        },
        deleteByDate: async function (dateObj) {
            const source = dateObj.source;
            const entity = dateObj.entity;
            const parent = dateObj.parent;
            const date = dateObj.date;

            if (confirm("Are you sure you want to delete?")) {
                console.log(
                    `DELETING data for date ${date} from experiment ${this.$route.params.code}`
                );

                await axios.delete(
                    `/experiment/${this.$route.params.code}/delete_mango_data/${date}/${entity}/${parent}`
                );

                EventBus.$emit("loadExperimentData");
            }
        },
        downloadCustomDataByDate: async function (dateObj) {
            const entity = dateObj.entity;
            const parent = dateObj.parent;
            const date = dateObj.date;
            const exp = this.$route.params.code;

            console.log(
                `Downloading ${entity} data for date ${date} from experiment ${this.$route.params.code}`
            );
            window.open(
                `${config.apiHost}/experiment/${exp}/excel/customDataByDate/${this.protocolId}/${entity}/${parent}/${date}/${exp}${entity}${date}Data.xlsx`,
                "_blank"
            );
        },
        // Legacy code to handle well data collection
        async wellDateSelectHandler(dateObj) {
            this.activeDateObj = { ...dateObj, label: "Benchling Well" };
            const date = dateObj.date;
            EventBus.$emit("globalLoadScreen", { status: true, message: "Loading Well Data" });
            this.loading = true;
            try {
                const res = await axios.get(`/experiment/${this.$route.params.code}/raw/${date}`);
                const response = res.data[0];
                const displayNames = res.data[1];

                this.headers = [
                    {
                        title: "QR Code",
                        value: "qr_code",
                        align: "left",
                        sortable: true,
                    },
                    {
                        title: "Well",
                        value: "well",
                        align: "left",
                        sortable: true,
                    },
                    {
                        title: "Condition #",
                        value: "condition_number",
                        align: "left",
                        sortable: true,
                        width: "10%",
                    },
                    {
                        title: "Condition Name",
                        value: "condition_name",
                        align: "left",
                        sortable: true,
                    },
                ];
                if (response.length) {
                    response.forEach((responseItem) => {
                        const dataArray = responseItem.data_array;
                        if (dataArray && dataArray.length) {
                            dataArray.forEach((dayData) => {
                                if (dayData && dayData.date_measured === date) {
                                    for (const key in dayData) {
                                        if (key !== "startDate") responseItem[key] = dayData[key];
                                    }
                                }
                                if (dataArray[0].live_count)
                                    responseItem.live_count = dataArray[0].live_count;
                            });
                        }
                    });

                    // Set data for v-data-table
                    this.rawData = response;

                    for (let property in response[0]) {
                        let found = [
                            "id",
                            "qr_code",
                            "application_number",
                            "well",
                            "date_measured",
                            "rawData",
                            "data_array",
                        ].includes(property);
                        if (!found && (displayNames[property] || property === "time_measured")) {
                            let header = {
                                title:
                                    property === "time_measured"
                                        ? "Time Measured"
                                        : displayNames[property],
                                value: property,
                                align: "left",
                                sortable: true,
                                class: "",
                            };
                            this.headers.push(header);
                        }
                    }
                }
                const assayRes = await axios.get(
                    `/benchling/type/${this.$route.params.protocolId}`
                );
                this.outputs = assayRes.data.length > 0 ? assayRes.data[0].outputs : [];
            } catch (error) {
                EventBus.$emit("message", { message: error });
            }
            // Close loading screen
            EventBus.$emit("globalLoadScreen", { status: false, message: null });
            this.loading = false;
        },
        async mangoDateSelectHandler(dateObj) {
            this.activeDateObj = dateObj;
            const entitySchema = _.find(
                this.mangoSchemaData,
                (schema) => schema.name === dateObj.entity && schema.parent === dateObj.parent
            );
            const entityData = _.find(
                this.experimentData.mangoData,
                (data) => data.name === dateObj.entity && data.parent === dateObj.parent
            );

            // Populate Headers
            const baseMangoHeaders = [
                {
                    title: "Entity",
                    value: "entity_name",
                    align: "left",
                    sortable: true,
                },
                {
                    title: "Entity Id",
                    value: "entity_id",
                    align: "left",
                    sortable: true,
                },
                {
                    title: "Collection Date",
                    value: "collection_date",
                    align: "left",
                    width: 150,
                    sortable: true,
                },
                {
                    title: "Collection Time",
                    value: "collection_time",
                    align: "left",
                    sortable: true,
                },
            ];

            // Map attributes to headers
            const attrOTHeaders = _.flatMap(entitySchema.attributes_ot, (attr) => {
                const headerObj = {
                    title: attr.display_name,
                    value: attr.name,
                    align: "left",
                    sortable: true,
                };
                // Handle 'numeric_w_units' widget type
                return attr.widget_type !== "numeric_w_units"
                    ? headerObj
                    : [
                          headerObj,
                          {
                              title: attr.display_name + " Units",
                              value: attr.name + "_units",
                              align: "left",
                              sortable: true,
                          },
                      ];
            });

            this.headers = baseMangoHeaders.concat(attrOTHeaders);

            // Populate Data
            const attrData = entityData.data.map((datum) => {
                return { ...datum, ...datum.ot_data };
            });
            const attrDataByDate = _.filter(attrData, { collection_date: dateObj.date });
            this.rawData = attrDataByDate;
        },
        async sampleDataSelectHandler(dateObj) {
            this.activeDateObj = dateObj;
            const date = dateObj.date;
            EventBus.$emit("globalLoadScreen", { status: true, message: "Loading Sample Data" });
            this.loading = true;
            try {
                const res = await axios.get(
                    `${config.apiHost}/samples/${this.$route.params.code}/${date}`
                );
                const response = res.data;
                this.headers = [
                    {
                        title: "ID",
                        value: "id",
                        align: "left",
                        sortable: true,
                    },
                    {
                        title: "Sample ID",
                        value: "sampleID",
                        align: "left",
                        sortable: true,
                    },
                    {
                        title: "Functional Key",
                        value: "functionalKey",
                        align: "left",
                        sortable: true,
                    },
                    {
                        title: "Sample Type",
                        value: "sampleType",
                        align: "left",
                        sortable: true,
                    },
                    {
                        title: "Sample Text",
                        value: "sampleText",
                        align: "left",
                        sortable: true,
                    },
                    {
                        title: "Sample Data",
                        value: "data",
                        align: "left",
                        sortable: false,
                    },
                    {
                        title: "Source Type",
                        value: "sourceType",
                        align: "left",
                        sortable: true,
                    },
                    {
                        title: "Source Name",
                        value: "sourceName",
                        align: "left",
                        sortable: true,
                    },
                    {
                        title: "Sample Protocol",
                        value: "protocolName",
                        align: "left",
                        sortable: true,
                    },
                ];
                this.rawData = response;
            } catch (error) {
                console.log(error);
            }
            // Close loading screen
            EventBus.$emit("globalLoadScreen", { status: false, message: null });
            this.loading = false;
        },
    },
});
</script>
